import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Linkedin, Instagram, Facebook, Mail, Clock } from 'lucide-react';
import { scrollToTop, isCurrentPage } from '../../utils/scrollToTop';
import './Header.css';

const Header = () => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isScrolled, setIsScrolled] = useState(false);
	const [activeSubmenu, setActiveSubmenu] = useState(null);
	const [submenuTimeout, setSubmenuTimeout] = useState(null);
	const location = useLocation();

	useEffect(() => {
		let ticking = false;
		const handleScroll = () => {
			if (ticking) return;
			ticking = true;
			window.requestAnimationFrame(() => {
				const next = window.scrollY > 50;
				setIsScrolled((prev) => (prev === next ? prev : next));
				ticking = false;
			});
		};

		window.addEventListener('scroll', handleScroll, { passive: true });
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	useEffect(() => {
		return () => {
			if (submenuTimeout) {
				clearTimeout(submenuTimeout);
			}
		};
	}, [submenuTimeout]);

	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
		// Lock body scroll when menu is open
		if (!isMenuOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
	};

	const closeMenu = () => {
		setIsMenuOpen(false);
		document.body.style.overflow = '';
	};

	const handleLinkClick = (path) => {
		// If clicking on current page, scroll to top
		if (isCurrentPage(location.pathname, path)) {
			scrollToTop();
		}
		// Close mobile menu if open
		closeMenu();
	};

	const handleSubmenuEnter = (itemName) => {
		// Clear any existing timeout
		if (submenuTimeout) {
			clearTimeout(submenuTimeout);
			setSubmenuTimeout(null);
		}
		// Set the new active submenu immediately (only if different)
		if (activeSubmenu !== itemName) {
			setActiveSubmenu(itemName);
		}
	};

	const handleSubmenuLeave = () => {
		// Clear any existing timeout first
		if (submenuTimeout) {
			clearTimeout(submenuTimeout);
		}
		// Set new timeout to close submenu with longer delay
		const timeout = setTimeout(() => {
			setActiveSubmenu(null);
		}, 300); // Increased delay for better UX
		setSubmenuTimeout(timeout);
	};

	const handleNavItemEnter = (item) => {
		// Always clear any existing timeout first
		if (submenuTimeout) {
			clearTimeout(submenuTimeout);
			setSubmenuTimeout(null);
		}

		if (item.submenu) {
			// Set the new submenu immediately
			setActiveSubmenu(item.name);
		} else {
			// If hovering over a non-submenu item, close any open submenu immediately
			setActiveSubmenu(null);
		}
	};

	const handleSubmenuClick = (path) => {
		setActiveSubmenu(null); // Close submenu immediately
		if (submenuTimeout) {
			clearTimeout(submenuTimeout);
			setSubmenuTimeout(null);
		}
		handleLinkClick(path);
	};

	const handleNavLeave = () => {
		// Only close if we're actually leaving the navigation area
		// Don't trigger if moving to submenu
		if (activeSubmenu && submenuTimeout === null) {
			handleSubmenuLeave();
		}
	};

	const navigationItems = [
		{ name: 'HOME', path: '/' },
		{
			name: 'SOBRE',
			path: '/somos',
			submenu: [
				{
					name: 'Nossa História',
					description: 'Fundada com foco em sustentabilidade',
					path: '/somos#nossa-historia',
				},
				{
					name: 'Missão e Valores',
					description: 'Energia limpa para todos os brasileiros',
					path: '/somos#missao-valores',
				},
			],
		},
		{
			name: 'NOSSAS SOLUÇÕES',
			path: '/sou-ener360',
			submenu: [
				{
					name: 'Sou ener360 - Licenciado',
					description: 'Oportunidades de negócio exclusivas',
					path: '/sou-ener360',
				},
				{
					name: 'Mercado de Energia',
					description: 'Gestão completa do seu contrato de energia',
					path: '/fluxo-comercializacao',
				},
				{
					name: 'Consultoria em Energia',
					description: 'Mercado livre e cativo com segurança jurídica',
					path: '/consultoria-energia',
				},
				{
					name: 'Seguro, Consórcio e Financiamento',
					description: 'Soluções financeiras para energia',
					path: '/seguro-consorcio-financiamento',
				},
				{
					name: 'Programa Indique e Ganhe',
					description: 'Seja pago por cada indicação confirmada',
					path: '/indique-e-ganhe',
				},
			],
		},
		{
			name: 'CLUBE',
			path: '/clube-ener360',
			submenu: [
				{
					name: 'Programa de Indicação',
					description: 'Ganhe dinheiro e novas fontes de receita com suas indicações',
					path: '/clube-ener360#entenda-campanha',
				},
				{
					name: 'Benefícios Exclusivos',
					description: 'PIX, desconto na fatura e viagens',
					path: '/clube-ener360',
				},
				{
					name: 'Vale-Viagem R$ 5.000',
					description: 'Campanha especial até setembro',
					path: '/clube-ener360',
				},
			],
		},
		{ name: 'BLOG', path: '/blog' },
		{ name: 'FAQ', path: '/faq' },
		{
			name: 'CONTATO',
			path: '/contato',
			submenu: [
				{
					name: 'Atendimento Rápido',
					description: 'Resposta em até 24 horas úteis',
					path: '/contato',
				},
				{
					name: 'Suporte Especializado',
					description: 'Equipe técnica qualificada',
					path: '/contato',
				},
			],
		},
	];

	const odsList = [
		{ number: 7, img: '/ods-7.jpeg', label: 'Energia Acessível e Limpa' },
		{ number: 9, img: '/ods-9.jpeg', label: 'Indústria, Inovação e Infraestrutura' },
		{ number: 13, img: '/ods-13.jpeg', label: 'Ação Contra a Mudança Global do Clima' },
		{ number: 14, img: '/ods-14.jpeg', label: 'Vida na Água' },
		{ number: 17, img: '/ods-17.jpeg', label: 'Parcerias e Meios de Implementação' },
	];

	return (
		<header className={`header ${isScrolled ? 'header--scrolled' : ''}`}>
			{/* Info Bar */}
			<div className="info-bar">
				<div className="container">
					<div className="info-content">
						<div className="info-left">
							<div className="info-item">
								<Mail size={16} />
								<a href="mailto:comercial@archman.com.br" className="info-link">
									comercial@archman.com.br
								</a>
							</div>
							<div className="info-item">
								<Clock size={16} />
								<span>Seg-Sex 8h às 18h</span>
							</div>
						</div>
						<div className="info-right">
							<div className="info-social-links">
								<a
									href="https://linkedin.com/company/ener360"
									target="_blank"
									rel="noopener noreferrer"
									className="info-social-link"
									aria-label="LinkedIn"
								>
									<Linkedin size={16} />
								</a>
								<a
									href="https://instagram.com/ener360oficial"
									target="_blank"
									rel="noopener noreferrer"
									className="info-social-link"
									aria-label="Instagram"
								>
									<Instagram size={16} />
								</a>
								<a
									href="https://www.facebook.com/profile.php?id=61582408901947"
									target="_blank"
									rel="noopener noreferrer"
									className="info-social-link"
									aria-label="Facebook"
								>
									<Facebook size={16} />
								</a>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* ODS Bar */}
			<div className="ods-bar">
				<div className="container">
					<div className="ods-content">
						<span className="ods-label">Tudo isso alinhado às ODS</span>
						<div className="ods-icons">
							{odsList.map((ods) => (
								<img
									key={ods.number}
									src={ods.img}
									alt={`ODS ${ods.number} - ${ods.label}`}
									title={`ODS ${ods.number} - ${ods.label}`}
									className="ods-icon"
									width={18}
									height={18}
									loading="lazy"
									decoding="async"
								/>
							))}
						</div>
					</div>
				</div>
			</div>

			{/* Main Header */}
			<div className="header-main">
				<div className="container">
					<div className="header-content">
						{/* Logo */}
						<Link to="/" className="logo" onClick={() => handleLinkClick('/')}>
							<img
								src="/logo-ener-green.svg"
								alt="ener360"
								className="logo-image"
								width={165}
								height={28}
								decoding="async"
							/>
						</Link>

						{/* Desktop Navigation */}
						<nav className="nav-desktop" onMouseLeave={handleNavLeave}>
							<ul className="nav-list">
								{navigationItems.map((item) => (
									<li
										key={item.name}
										className={`nav-item ${
											item.submenu ? 'nav-item--has-submenu' : ''
										}`}
										onMouseEnter={() => handleNavItemEnter(item)}
									>
										<Link
											to={item.path}
											className={`nav-link ${
												location.pathname === item.path
													? 'nav-link--active'
													: ''
											} ${
												activeSubmenu === item.name
													? 'nav-link--submenu-active'
													: ''
											}`}
											onClick={() => handleLinkClick(item.path)}
										>
											{item.name}
										</Link>

										{/* Submenu */}
										{item.submenu && (
											<div
												className={`submenu ${
													activeSubmenu === item.name ? 'submenu--visible' : ''
												}`}
												onMouseEnter={() => handleSubmenuEnter(item.name)}
											>
												<ul className="submenu-list">
													{item.submenu.map((subItem) => (
														<li key={subItem.name} className="submenu-item">
															<Link
																to={subItem.path}
																className="submenu-content"
																onClick={() => handleSubmenuClick(subItem.path)}
															>
																<div className="submenu-text-content">
																	<span className="submenu-title">
																		{subItem.name}
																	</span>
																	<span className="submenu-description">
																		{subItem.description}
																	</span>
																</div>
															</Link>
														</li>
													))}
												</ul>
											</div>
										)}
									</li>
								))}
							</ul>
						</nav>

						{/* Header Actions */}
						<div className="header-actions">
							<div className="header-buttons">
								<a
									href="https://ener360.tech/login"
									target="_blank"
									rel="noopener noreferrer"
									className="btn btn-secondary btn-sm"
								>
									Entrar
								</a>
								<Link
									to="/contato"
									className="btn btn-primary btn-sm"
									onClick={() => handleLinkClick('/contato')}
								>
									Contato
								</Link>
							</div>

							{/* Mobile Menu Toggle */}
							<button
								className="mobile-menu-toggle"
								onClick={toggleMenu}
								aria-label="Toggle menu"
							>
								{isMenuOpen ? <X size={24} /> : <Menu size={24} />}
							</button>
						</div>
					</div>
				</div>
			</div>

			{/* Mobile Navigation Overlay */}
			<div
				className={`nav-mobile-overlay ${
					isMenuOpen ? 'nav-mobile-overlay--visible' : ''
				}`}
				onClick={closeMenu}
			/>

			{/* Mobile Navigation */}
			<nav className={`nav-mobile ${isMenuOpen ? 'nav-mobile--open' : ''}`}>
				{/* Close Button */}
				<button
					className="nav-mobile-close"
					onClick={closeMenu}
					aria-label="Fechar menu"
				>
					<X size={28} />
				</button>

				<div className="nav-mobile-content">
					<ul className="nav-mobile-list">
						{navigationItems.map((item) => (
							<li key={item.name} className="nav-mobile-item">
								<Link
									to={item.path}
									className={`nav-mobile-link ${
										location.pathname === item.path
											? 'nav-mobile-link--active'
											: ''
									}`}
									onClick={() => handleLinkClick(item.path)}
								>
									{item.name}
								</Link>
							</li>
						))}
					</ul>

					<div className="nav-mobile-actions">
						<div className="nav-mobile-social">
							<a
								href="https://linkedin.com/company/ener360"
								target="_blank"
								rel="noopener noreferrer"
								className="nav-mobile-social-link"
								aria-label="LinkedIn"
							>
								<Linkedin size={24} />
							</a>
							<a
								href="https://instagram.com/ener360oficial"
								target="_blank"
								rel="noopener noreferrer"
								className="nav-mobile-social-link"
								aria-label="Instagram"
							>
								<Instagram size={24} />
							</a>
							<a
								href="https://www.facebook.com/profile.php?id=61582408901947"
								target="_blank"
								rel="noopener noreferrer"
								className="nav-mobile-social-link"
								aria-label="Facebook"
							>
								<Facebook size={24} />
							</a>
						</div>

						<div className="nav-mobile-buttons">
							<a
								href="https://ener360.tech/login"
								target="_blank"
								rel="noopener noreferrer"
								className="btn btn-secondary"
								onClick={closeMenu}
							>
								Entrar
							</a>
							<Link
								to="/contato"
								className="btn btn-primary"
								onClick={() => handleLinkClick('/contato')}
							>
								Contato
							</Link>
						</div>
					</div>
				</div>
			</nav>
		</header>
	);
};

export default Header;
